import { SharedFillRow } from './types';

export const COVER_FIT = { objectFit: 'cover' as const, width: '100%' as const, height: '100%' as const };

export const BORDER_RADIUS_8 = '8px';
export const NO_RADIUS = '0px';

/** Target tile width:height ratio for TILES layout.
 *  Matches 16:9 webcam feeds so `objectFit: cover` crops minimally.
 *  Row heights scale proportionally — wider rows become taller
 *  so every tile shares the same effective AR. */
export const TARGET_TILE_AR = 16 / 9;

/** 4:3 speaker/PiP tile ratio for FILM_STRIP and PIP (rectangular).
 *  Used when `keepAspectRatio` is off (the default); when on, those layouts
 *  switch to `TARGET_TILE_AR` (16:9) to match the source webcam feeds. */
export const SCREENSHARE_TILE_AR_4_3 = 4 / 3;

export const SHARED_FILL_TILE_AR = 4 / 2;
export const PORTRAIT_SHARED_FILL_TILE_AR = 1;

export const PIP_POSITION_STYLES: Record<string, Record<string, string>> = {
  LEFT_TOP: { top: '1.5%', bottom: 'auto', left: '1%', right: 'auto' },
  LEFT_BOTTOM: { top: 'auto', bottom: '1.5%', left: '1%', right: 'auto' },
  RIGHT_TOP: { top: '1.5%', bottom: 'auto', left: 'auto', right: '1%' },
  RIGHT_BOTTOM: { top: 'auto', bottom: '1.5%', left: 'auto', right: '1%' },
  TOP_MIDDLE: { top: '1.5%', bottom: 'auto', left: '50%', right: 'auto', transform: 'translateX(-50%)' },
  BOTTOM_MIDDLE: { top: 'auto', bottom: '1.5%', left: '50%', right: 'auto', transform: 'translateX(-50%)' },
  LEFT_MIDDLE: { top: '50%', bottom: 'auto', left: '1%', right: 'auto', transform: 'translateY(-50%)' },
  RIGHT_MIDDLE: { top: '50%', bottom: 'auto', left: 'auto', right: '1%', transform: 'translateY(-50%)' }
};

export const PIP_TRANSITION = 'top 0.25s ease, left 0.25s ease, right 0.25s ease, bottom 0.25s ease';

export const GRID_DISTRIBUTION: Record<number, number[]> = {
  1: [1],
  2: [2],
  3: [2, 1],
  4: [2, 2],
  5: [2, 3],
  6: [3, 3],
  7: [3, 4],
  8: [2, 3, 3],
  9: [3, 3, 3],
  10: [3, 3, 4],
  11: [3, 4, 4],
  12: [3, 4, 5],
  13: [3, 5, 5],
  14: [3, 3, 4, 4],
  15: [3, 3, 4, 5],
  16: [3, 3, 5, 5],
  17: [3, 4, 5, 5],
  18: [3, 5, 5, 5],
  19: [4, 5, 5, 5],
  20: [5, 5, 5, 5],
  21: [4, 4, 4, 4, 5],
  22: [4, 4, 4, 5, 5],
  23: [4, 4, 5, 5, 5],
  24: [4, 5, 5, 5, 5]
};

export const GRID_DISTRIBUTION_PORTRAIT: Record<number, number[]> = {
  ...GRID_DISTRIBUTION,
  2: [1, 1],
  6: [2, 2, 2],
  7: [2, 2, 3]
};

export const GRID_DISTRIBUTION_PORTRAIT_WITH_SCREEN: Record<number, number[]> = {
  ...GRID_DISTRIBUTION,
  6: [2, 2, 2],
  7: [2, 2, 3]
};

// Square grid distribution
// Rule: rows = ceil(n/5); split n across rows non-decreasingly (push remainder onto later rows).
// Verified against Figma §A/§B for drawn counts; §A/§B frames at n=13/14 for §B are stale
// landscape copy-pastes — §A values are authoritative.
//   Drawn (§A/§B):  n=1–14
//   Extrapolated:   n=15–24  (not design-reviewed)
export const GRID_DISTRIBUTION_SQUARE: Record<number, number[]> = {
  1: [1],
  2: [2],
  3: [2, 1],
  // drawn
  4: [2, 2],
  5: [2, 3],
  6: [3, 3],
  7: [3, 4],
  8: [4, 4],
  9: [3, 3, 3],
  10: [3, 3, 4],
  11: [3, 4, 4],
  12: [4, 4, 4],
  13: [4, 4, 5],
  14: [4, 5, 5],
  15: [5, 5, 5],
  16: [4, 4, 4, 4],
  17: [4, 4, 4, 5],
  18: [4, 4, 5, 5],
  19: [4, 5, 5, 5],
  20: [5, 5, 5, 5],
  21: [4, 4, 4, 4, 5],
  22: [4, 4, 4, 5, 5],
  23: [4, 4, 5, 5, 5],
  24: [4, 5, 5, 5, 5]
};

// FILL differs from base at n=2: §A stacks two speakers (one per row).
export const GRID_DISTRIBUTION_SQUARE_FILL: Record<number, number[]> = {
  ...GRID_DISTRIBUTION_SQUARE,
  2: [1, 1]
};

// SHARED_FILL speaker band: §C shows 3 and 4 speakers on one wide row.
export const SQUARE_SHARED_FILL_DISTRIBUTION: Record<number, number[]> = {
  ...GRID_DISTRIBUTION_SQUARE,
  3: [3],
  4: [4]
};

// §C: speaker band height as a fraction of (usableH − gap between sections).
export const SQUARE_SHARED_FILL_SPEAKER_BAND_FRACTION = 324 / 828;

// §D: film-strip tile height ceiling as a fraction of stage height H.
export const SQUARE_FILM_STRIP_TILE_HEIGHT_FRACTION = 200 / 900;

// §E: PIP rectangular overlay width as a fraction of the contained screen width.
export const SQUARE_PIP_WIDTH_FRACTION = 256 / 900;

export const SHARED_FILL_DISTRIBUTION: Record<number, SharedFillRow[]> = {
  1: [{ cols: 1, fullWidth: true, scaleRatio: 2.6 }],
  2: [
    { cols: 1, scaleRatio: 1.4 },
    { cols: 1, scaleRatio: 1.4 }
  ],
  3: [
    { cols: 1, fullWidth: true, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.8 }
  ],
  4: [
    { cols: 2, scaleRatio: 2 },
    { cols: 2, scaleRatio: 2 }
  ],
  5: [
    { cols: 1, fullWidth: true, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.5 },
    { cols: 2, scaleRatio: 1.5 }
  ],
  6: [
    { cols: 2, scaleRatio: 1.8 },
    { cols: 2, scaleRatio: 1.8 },
    { cols: 2, scaleRatio: 1.8 }
  ],
  7: [
    { cols: 1, fullWidth: true, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 }
  ],
  8: [
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 },
    { cols: 2, scaleRatio: 1.4 }
  ],
  9: [{ cols: 2 }, { cols: 2 }, { cols: 2 }, { cols: 3 }],
  10: [{ cols: 2 }, { cols: 2 }, { cols: 3 }, { cols: 3 }],
  11: [{ cols: 2 }, { cols: 3 }, { cols: 3 }, { cols: 3 }],
  12: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }],
  13: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 1 }],
  14: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 2 }],
  15: [{ cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }, { cols: 3 }],
  16: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }],
  17: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 1 }],
  18: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 2 }],
  19: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 3 }],
  20: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }],
  21: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 1 }],
  22: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 2 }],
  23: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 3 }],
  24: [{ cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }, { cols: 4 }]
};
